/**
 * Keyboard shortcuts for the app shell.
 *
 * The list here is the single source of truth: the handler binds from it and
 * the help dialog renders from it, so a shortcut cannot be advertised without
 * working, or work without being discoverable.
 */

export interface Shortcut {
  /** Section heading in the help dialog. */
  group: 'Navigation' | 'Quick actions' | 'Help';
  /** What it does, in the user's words. */
  label: string;
  /** Rendered keys, in order. `g` then `c` becomes ['g', 'c']. */
  keys: string[];
  /** A two-key sequence such as `g` then `c`, rather than a chord. */
  sequence?: boolean;
  /** Route to navigate to, for navigation shortcuts. */
  href?: string;
  /** Named action for anything that is not a plain navigation. */
  action?: 'openSearch' | 'openDialer' | 'showShortcuts';
}

export const SHORTCUTS: Shortcut[] = [
  { group: 'Navigation', label: 'Go to Activity', keys: ['g', 'a'], sequence: true, href: '/dashboard' },
  { group: 'Navigation', label: 'Go to Contacts', keys: ['g', 'c'], sequence: true, href: '/contacts' },
  { group: 'Navigation', label: 'Go to Call logs', keys: ['g', 'l'], sequence: true, href: '/call-records' },
  { group: 'Navigation', label: 'Go to Analytics', keys: ['g', 'n'], sequence: true, href: '/analytics' },
  { group: 'Navigation', label: 'Go to AI Receptionist', keys: ['g', 'r'], sequence: true, href: '/voice-agents' },
  { group: 'Navigation', label: 'Go to Settings', keys: ['g', 's'], sequence: true, href: '/settings' },
  { group: 'Navigation', label: 'Go to Ask LimeAI', keys: ['g', 'i'], sequence: true, href: '/assistant' },

  { group: 'Quick actions', label: 'Search', keys: ['/'], href: '/search' },
  { group: 'Quick actions', label: 'Open the dialer', keys: ['d'], action: 'openDialer' },

  { group: 'Help', label: 'Show keyboard shortcuts', keys: ['?'], action: 'showShortcuts' },
];

/** How long a `g`-prefixed sequence stays open, in milliseconds. */
export const SEQUENCE_WINDOW_MS = 1200;

/**
 * Whether a keystroke should be ignored because the user is typing.
 *
 * Without this, `g` inside a message composer would navigate away mid-sentence.
 * Covers inputs, textareas, native selects and anything contenteditable —
 * including rich text editors, which are contenteditable rather than inputs.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;

  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;

  // A dialog trapping focus is usually a form; leave its keys alone.
  return el.closest('[role="dialog"]') !== null;
}

/** Groups in the order the help dialog shows them. */
export function groupedShortcuts(): Array<[Shortcut['group'], Shortcut[]]> {
  const order: Shortcut['group'][] = ['Navigation', 'Quick actions', 'Help'];
  return order
    .map((g) => [g, SHORTCUTS.filter((s) => s.group === g)] as [Shortcut['group'], Shortcut[]])
    .filter(([, items]) => items.length > 0);
}

/** Finds a single-key shortcut, ignoring anything with a modifier held. */
export function matchSingleKey(key: string): Shortcut | undefined {
  return SHORTCUTS.find((s) => !s.sequence && s.keys.length === 1 && s.keys[0] === key);
}

/** Finds a two-key sequence, e.g. `g` then `c`. */
export function matchSequence(first: string, second: string): Shortcut | undefined {
  return SHORTCUTS.find((s) => s.sequence && s.keys[0] === first && s.keys[1] === second);
}
